import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, CardTitle, Spinner } from "reactstrap";
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { apiURL } from '../../../config';
import Footer from "components/Footers/Footer";

const TeacherDashboard = () => {
  const token = localStorage.getItem("token");

  const [teacherInfo, setTeacherInfo] = useState({ name: '', email: '' });
  const [attendanceSummary, setAttendanceSummary] = useState({ Present: 0, Absent: 0, Leave: 0 });
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const user = localStorage.getItem('userInfo');
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        setUserId(parsedUser._id);
      } catch (error) {
        console.error("Error parsing user info from localStorage:", error);
        setError("Failed to load user info.");
        setLoading(false);
        return;
      }
    } else {
      setError("User info not found in localStorage.");
      setLoading(false);
      return;
    }
  }, []);

  useEffect(() => {
    if (!userId) return;

    const fetchTeacherData = async () => {
      setLoading(true);
      try {
        const infoResponse = await axios.get(`${apiURL}/teacher/${userId}/teacher-info`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        setTeacherInfo(infoResponse.data || { name: '', email: '' });

        const summaryResponse = await axios.get(`${apiURL}/teacher/${userId}/techer-summary`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        setAttendanceSummary(summaryResponse.data?.attendanceSummary || { Present: 0, Absent: 0, Leave: 0 });

        const subjectsResponse = await axios.get(`${apiURL}/teacher/${userId}/subjects`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        setSubjects(subjectsResponse.data || []);

        const classesResponse = await axios.get(`${apiURL}/teacher/${userId}/instructor`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        setClasses(classesResponse.data || []);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching staff data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherData();
  }, [userId, token]);

  if (loading) {
    return <p>Loading Staff data...</p>;
  }

  if (error) {
    return <p>NO RECORD FOUND</p>;
  }

  const attendanceData = [
    { name: 'Present', value: parseFloat(attendanceSummary?.Present || 0), color: '#2dce89' },
    { name: 'Absent', value: parseFloat(attendanceSummary?.Absent || 0), color: '#f5365c' },
    { name: 'Leave', value: parseFloat(attendanceSummary?.Leave || 0), color: '#172b4d' },
  ];

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Staff Information</CardTitle>
              {teacherInfo ? (
                <>
                  <p><strong>Name:</strong> {teacherInfo.name}</p>
                  <p><strong>Email:</strong> {teacherInfo.email}</p>
                </>
              ) : (
                <p>No staff information available.</p>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Classes Instructed</CardTitle>
              {classes && classes.length > 0 ? (
                <div className="d-flex flex-wrap">
                  {classes.map((className, index) => (
                    <span key={index} className="badge bg-primary text-white me-2 mb-2 ml-2" style={{ fontSize: '1rem', padding: '0.5em 0.8em' }}>{className}</span>
                  ))}
                </div>
              ) : (
                <p>No classes assigned.</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Attendance Summary</CardTitle>
              {attendanceSummary && (
                <>
                  <div className="d-flex justify-content-around align-items-center mb-3">
                    <div className="text-center">
                      <div className="font-weight-bold text-success">{attendanceSummary.Present || 0}</div>
                      <div>Present</div>
                    </div>
                    <div className="text-center">
                      <div className="font-weight-bold text-danger">{attendanceSummary.Absent || 0}</div>
                      <div>Absent</div>
                    </div>
                    <div className="text-center">
                      <div className="font-weight-bold text-info">{attendanceSummary.Leave || 0}</div>
                      <div>Leave</div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={attendanceData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {attendanceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Subjects Taught</CardTitle>
              {subjects && subjects.length > 0 ? (
                <Row>
                  {subjects.map((item, index) => (
                    <Col md="6" key={index} className="mb-2">
                      <Card className="shadow-sm">
                        <CardBody>
                          <CardTitle tag="h6" className="mb-1">{item.subject.name}</CardTitle>
                          <small className="text-muted">Code: {item.subject.subjectCode}</small>
                          {item.classes && item.classes.length > 0 && (
                            <div className="mt-2">
                              <small className="text-muted">Class</small>

                              {item.classes.map((className, classIndex) => (
                                <span key={classIndex} className="badge bg-secondary text-black me-1" style={{ fontSize: '0.8rem', padding: '0.3em 0.5em' }}>{className}</span>
                              ))}
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <p>No subjects assigned.</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Footer />
    </Container>
  );
};

export default TeacherDashboard;
